import { AsyncSelect, AsyncSelectProps } from "@sembark-travel/ui-base"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import {
	ITag,
	TType,
	TTagScope,
	TTagFilters,
	TFiltersInLocationQuery,
	IOrgTag,
	TAG_SCOPES,
	filtersToQuery,
	queryToFilters,
	filtersToAPIParams,
} from "./store"

import {
	Box,
	Button,
	CheckboxInput,
	Dropdown,
	Icons,
	Inline,
	RelativeTime,
	Stack,
	Table,
	Text,
	Tooltip,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	Form,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { ListView, Search, useSearch } from "@sembark-travel/ui-list"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { ICursorListResponse, IListResponse } from "@sembark-travel/xhr"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import useSWR from "swr"
import * as validator from "yup"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { generatePath } from "../router-utils"
import { checkFilterAppliedFn } from "../utils"
import { TagBadge } from "./TagBadges"

interface SelectTagsProps extends Omit<AsyncSelectProps, "fetch"> {
	type: TType
}

function isOrgTagScope(type: TType): type is TTagScope {
	return type in TAG_SCOPES
}

function TagsXHR(xhr: XHRInstance) {
	return {
		async getTagsForSelect(type: TType, q?: string): Promise<Array<ITag>> {
			if (isOrgTagScope(type)) {
				// Centralized org tags, filtered by scope ("trips").
				return xhr
					.get("/tags", { params: { q, module: type } })
					.then((resp) => resp.data.data)
			}
			// Legacy per-entity tags for everything else.
			return xhr
				.get(`/${type}-tags`, { params: { q } })
				.then((resp) => resp.data.data)
		},

		async destroy(tagId: number | string) {
			return xhr
				.delete<{ message: string }>(`/tags/${tagId}`)
				.then((resp) => resp.data)
		},

		async getTags(
			params?: Record<string, unknown>
		): Promise<IListResponse<IOrgTag>> {
			return xhr
				.get("/tags", {
					params: {
						pagination: "paginate",
						...params,
					},
				})
				.then((resp) => resp.data)
		},

		async getTagsWithCursor(
			params?: Record<string, unknown>
		): Promise<ICursorListResponse<IOrgTag>> {
			return xhr
				.get("/tags", {
					params: {
						pagination: "cursor",
						...params,
					},
				})
				.then((resp) => resp.data)
		},
	}
}

interface ITagsList {
	scope: TTagScope
}

export function TagsList({ scope }: ITagsList) {
	const [query, setQuery] = useLocationQuery<
		TTagFilters,
		TFiltersInLocationQuery
	>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TTagFilters>(query)
	const [componentKey, setComponentKey] = useState(0)
	const { hasPermission } = useCheckPermissions()

	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])

	const permission = hasPermission(PERMISSIONS.MANAGE_TAGS)
	return (
		<Search
			title={
				<Inline gap="2" alignItems="center">
					<Text>{TAG_SCOPES[scope].label} Tags</Text>

					<Tooltip content="This section includes tags related to trips and leads.">
						<Box>
							<Icons.Info />
						</Box>
					</Tooltip>
				</Inline>
			}
			initialParams={params}
			onSearch={(params) => {
				setParams({
					...params,
					page: 1,
					cursor: null,
				})
			}}
			Filters={Filters}
			areAdvancedFiltersApplied={(params) => {
				return Boolean(params.unused)
			}}
			actions={({ changeAdvancedFiltersVisibility }) =>
				permission ? (
					<Inline gap="2">
						<ButtonLink
							to={generatePath(`/tags/${scope}/new`)}
							level="primary"
							status="primary"
							anchored
						>
							Add New
						</ButtonLink>
						<Dropdown alignRight="sm">
							<Dropdown.ToggleButton level="tertiary">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<Dropdown.MenuItem
									as={Link}
									to={generatePath(`/tags/${scope}/merge`)}
								>
									<Icons.Merge /> Merge Duplicate Tags
								</Dropdown.MenuItem>
								<BulkDeleteTags
									scope={scope}
									params={params}
									onSuccess={() => {
										setParams((prev) => ({
											...prev,
											page: 1,
											cursor: null,
										}))

										setComponentKey((key) => key + 1)
									}}
									openFilter={() => {
										changeAdvancedFiltersVisibility(true)
									}}
								>
									{({ onDelete }) => (
										<Dropdown.MenuItem onClick={onDelete}>
											<Icons.Trash color="danger" /> Bulk Delete Tags
										</Dropdown.MenuItem>
									)}
								</BulkDeleteTags>
							</Dropdown.Menu>
						</Dropdown>
					</Inline>
				) : null
			}
		>
			<ListView<IOrgTag>
				pageKey={`tags-${scope}`}
				key={componentKey}
				onPageChange={(page) => setParams({ ...params, page })}
				params={params}
				fetch={(xhr, params) => {
					return TagsXHR(xhr).getTags({
						...filtersToAPIParams(params),
						module: scope,
						include: "created_by",
					})
				}}
			>
				{({ items: tags, refresh, xhr }) => (
					<Table
						bordered
						headers={[
							"Title",
							<Inline gap="2" minWidth="min" justifyContent={"center"}>
								<Text>Usage</Text>

								<Tooltip content="Usage represents how many trips and leads are currently using this tag.">
									<Box style={{ display: "flex", alignItems: "center" }}>
										<Icons.Info />
									</Box>
								</Tooltip>
							</Inline>,
							"Created By",
							"",
						]}
						responsive
						alignCols={{ 3: "right", 1: "center", 2: "right" }}
						rows={tags.map((tag) => {
							const isDeleteDisabled = (tag.usage_count || 0) > 0

							return [
								<Stack gap="1" alignItems="start">
									<TagBadge tag={tag} />
									{tag.description && (
										<Text color="muted" fontSize="sm">
											{tag.description}
										</Text>
									)}
								</Stack>,
								<Text>{tag?.usage_count || 0}</Text>,

								<>
									{tag.created_by && (
										<>
											<Text>{tag.created_by?.name} </Text>
											<RelativeTime
												timestamp={tag.created_at}
												color="muted"
												fontSize="sm"
											/>
										</>
									)}
								</>,
								<Inline gap="4" justifyContent="end">
									{permission ? (
										isDeleteDisabled ? (
											<Tooltip
												content={`This tag is currently in used. Please merge or remove its usage before deleting the tag.`}
											>
												<Button
													level="tertiary"
													size="sm"
													status="warning"
													disabled
												>
													<Icons.Trash />
												</Button>
											</Tooltip>
										) : (
											<Button
												level="tertiary"
												size="sm"
												status="warning"
												onClick={() => {
													if (
														window.confirm(
															`Are you sure you want to delete this tag?\nThis action CAN NOT be reverted.`
														)
													) {
														TagsXHR(xhr)
															.destroy(tag.id)
															.then((resp) => {
																showSnackbar(
																	resp.message || "Tag deleted successfully."
																)

																refresh()
															})
															.catch((e) => {
																const error: Error = e

																window.alert(
																	error.message || "Something went wrong"
																)
															})
													}
												}}
											>
												<Icons.Trash />
											</Button>
										)
									) : null}
								</Inline>,
							]
						})}
					/>
				)}
			</ListView>
		</Search>
	)
}

function Filters() {
	return <SwitchInputField name="unused" label="Show unused tags only" />
}

interface RefHandle {
	getUnSelectedIds: () => number[]
	isItemZero: boolean
}
function BulkDeleteTags({
	children,
	params,
	onSuccess,
	openFilter,
	scope,
}: {
	children: (props: { onDelete: () => void }) => React.ReactNode
	params: TTagFilters
	onSuccess: () => void
	openFilter: () => void
	scope: TTagScope
}) {
	const xhr = useXHR()
	const [isOpen, openDialog, closeDialog] = useDialog()
	const inProgressRef = useRef(false)
	const [inProgressState, setInProgressState] = useState(false)
	const [isSelectAll, setIsSelectAll] = useState(false)
	const childRef = useRef<RefHandle>(null)
	const areFiltersApplied = useMemo(
		() => checkFilterAppliedFn(params, "cursor", "page"),
		[params]
	)
	const [count, setCount] = useState<{
		count: number
		totalCount: number
	}>({
		count: 0,
		totalCount: 0,
	})

	useEffect(() => {
		if (!inProgressRef.current && inProgressState) {
			showSnackbar("Deleting...")
		}
		if (inProgressRef.current && !inProgressState) {
			showSnackbar("Deleted Successfully...")
			closeDialog()
		}
		inProgressRef.current = inProgressState
	}, [inProgressState, closeDialog])

	useEffect(() => {
		async function getCount() {
			try {
				const item = await TagsXHR(xhr).getTags({
					...filtersToAPIParams(params),
					limit: 1,
					page: 1,
					module: scope,
				})
				setCount({
					count: 0,
					totalCount: item.meta.total || 0,
				})
			} catch (e) {
				const error = e as Error
				window.alert(error.message)
			}
		}

		if (isOpen && areFiltersApplied) {
			getCount()
		}

		if (!isOpen) {
			setCount({
				count: 0,
				totalCount: 0,
			})
			setIsSelectAll(false)
		}
	}, [isOpen, params, areFiltersApplied, xhr, scope])

	const decreaseIncrease = (type: "decrease" | "increase") => {
		setCount((val) => ({
			...val,
			count: type === "decrease" ? val.count - 1 : val.count + 1,
		}))
	}

	const closeDialogAndOpenFilter = () => {
		closeDialog()
		openFilter()
	}
	return (
		<Batch onFinish={() => onSuccess()}>
			{({ init, xhr, inProgress }) => {
				if (inProgress !== inProgressState) {
					setInProgressState(inProgress)
				}
				return (
					<>
						{children({ onDelete: () => openDialog() })}
						<Dialog
							title="Bulk Delete Tags"
							open={isOpen}
							onClose={closeDialog}
							lg
						>
							<Form<{ delete_tags_count: number }>
								validate={validateFormValues(
									validator.object().shape({
										delete_tags_count: validator
											.number()
											.equals(
												[count.count],
												`Please type ${count.count} to confirm the selection`
											)
											.not([0], "Please type a valid number")
											.required(
												`Please type ${count.count} to confirm the selection`
											),
									})
								)}
								onSubmit={withServerErrors(async () => {
									const resp = await xhr.delete("/tags-bulk-destroy", {
										data: {
											...filtersToAPIParams(params),
											module: scope,
											...(isSelectAll
												? {
														excluded_ids: childRef.current
															?.getUnSelectedIds()
															.map((id) => id),
													}
												: {
														included_ids: childRef.current
															?.getUnSelectedIds()
															.map((id) => id),
													}),
										},
									})
									init(resp.data.data)
								})}
								subscription={{ submitting: true }}
							>
								{({ handleSubmit, submitting }) => (
									<form onSubmit={handleSubmit}>
										<Dialog.Body>
											<Inline justifyContent="between">
												<Text fontSize="lg" fontWeight="semibold">
													Please review details
												</Text>
												{!isSelectAll &&
												areFiltersApplied &&
												!childRef.current?.isItemZero ? (
													<Inline alignItems="center">
														<CheckboxInput
															checked={isSelectAll}
															name="selected-all"
															id="selected_all"
															onChange={(e) => {
																setIsSelectAll(true)
																setCount((val) => ({
																	...val,
																	count: val.totalCount,
																}))
															}}
														/>

														<Box as="label" htmlFor="selected_all">
															Select All
														</Box>
													</Inline>
												) : null}
											</Inline>
											<InfiniteScroll
												params={params}
												openFilter={closeDialogAndOpenFilter}
												isSelectAll={isSelectAll}
												ref={childRef}
												decreaseIncrease={decreaseIncrease}
												scope={scope}
											/>
											{areFiltersApplied && !childRef.current?.isItemZero ? (
												<Stack gap="4" paddingTop="4">
													<Text color="danger">
														<Icons.Info /> Deleting these tags is permanent. You
														can't restore them later.
													</Text>
													{count.count ? (
														<TextInputField
															name="delete_tags_count"
															label={`Please Type ${count.count} to confirm`}
															type="number"
															required
														/>
													) : null}
												</Stack>
											) : null}
										</Dialog.Body>
										{areFiltersApplied && !childRef.current?.isItemZero ? (
											<Dialog.Footer>
												<Stack gap={"4"}>
													<SubmissionError />
													<Inline gap={"2"}>
														<Button
															type="submit"
															disabled={submitting || !count.count}
															status="danger"
														>
															<Icons.Ok /> I understand, delete these Tags
														</Button>
														<Button
															level="tertiary"
															onClick={() => closeDialog()}
														>
															Cancel
														</Button>
													</Inline>
												</Stack>
											</Dialog.Footer>
										) : null}
									</form>
								)}
							</Form>
						</Dialog>
					</>
				)
			}}
		</Batch>
	)
}

type TInfiniteScrollBase = {
	params: TTagFilters
	openFilter: () => void
	isSelectAll: boolean
	decreaseIncrease: (type: "decrease" | "increase") => void
	scope: TTagScope
}

type CursorMeta = ICursorListResponse<IOrgTag>["meta"]

const InfiniteScroll = React.forwardRef<RefHandle, TInfiniteScrollBase>(
	InfiniteScrollBase
)

function InfiniteScrollBase(
	{
		params,
		decreaseIncrease,
		openFilter,
		isSelectAll,
		scope,
	}: TInfiniteScrollBase,
	ref: React.ForwardedRef<RefHandle>
) {
	const groupRef = useRef<HTMLDivElement>(null)
	const pageInfo = useRef<CursorMeta>()
	const isFetchingRef = useRef(false)
	const SCROLL_THRESHOLD = 100
	const [items, setItems] = useState<IOrgTag[]>([])
	const [selectedItems, setSelectedItems] = useState<number[]>([])

	type Status = {
		isLoading: boolean
		error: string
	}

	const [status, setStatus] = useState<Status>({
		isLoading: false,
		error: "",
	})
	const xhr = useXHR()
	const getUnusedTags = useCallback(async () => {
		if (isFetchingRef.current) return
		setStatus((val) => ({
			...val,
			isLoading: true,
		}))
		isFetchingRef.current = true
		try {
			const item = await TagsXHR(xhr).getTagsWithCursor({
				...filtersToAPIParams(params),
				limit: 10,
				cursor: pageInfo.current?.next_cursor,
				module: scope,
			})
			pageInfo.current = item.meta

			setItems((prev) =>
				pageInfo.current?.prev_cursor ? [...prev, ...item.data] : [...item.data]
			)
			setStatus((val) => ({ error: "", isLoading: false }))
		} catch (e) {
			const error = e as Error
			setStatus((val) => ({
				...val,
				error: error.message || "Something went Wrong",
			}))
		} finally {
			isFetchingRef.current = false
		}
	}, [xhr, params, scope])

	useEffect(() => {
		if (checkFilterAppliedFn(params, "cursor", "page")) {
			getUnusedTags()
		}
		let throttleTimeout: ReturnType<typeof setTimeout> | null = null

		const throttledHandleScroll = () => {
			if (throttleTimeout) return
			throttleTimeout = setTimeout(() => {
				const container = groupRef.current?.querySelector("ol")
				if (!container) return

				const isBottom =
					container.scrollHeight - container.scrollTop <=
					container.clientHeight + SCROLL_THRESHOLD

				if (
					isBottom &&
					!isFetchingRef.current &&
					pageInfo.current?.next_cursor
				) {
					getUnusedTags()
				}
				throttleTimeout = null
			}, 300)
		}

		const container = groupRef.current?.querySelector("ol")
		if (container) {
			container.addEventListener("scroll", throttledHandleScroll)
			return () => {
				container.removeEventListener("scroll", throttledHandleScroll)
				if (throttleTimeout) {
					clearTimeout(throttleTimeout)
				}
			}
		}
	}, [getUnusedTags, params])

	React.useImperativeHandle(ref, () => ({
		getUnSelectedIds: function () {
			return selectedItems
		},
		isItemZero: !items.length && !status.isLoading,
	}))

	useEffect(() => {
		setSelectedItems([])
	}, [isSelectAll])

	return (
		<Stack
			padding="4"
			borderWidth="1"
			borderColor="warning"
			rounded="lg"
			ref={groupRef}
		>
			{status.error ? (
				<Inline
					alignItems="center"
					justifyContent="between"
					gap={"3"}
					padding={"3"}
					borderColor={"danger"}
					borderRadius="md"
				>
					<Text fontWeight="semibold">{status.error}</Text>
					<Button onClick={getUnusedTags}>Refresh</Button>
				</Inline>
			) : null}
			<Box style={{ height: "400px" }} as="ol" overflowY="auto">
				{!items.length && !status.isLoading ? (
					<Stack
						height="full"
						justifyContent="center"
						alignItems="center"
						gap="3"
					>
						<Text>
							Please choose the right filter or search option to delete tags
						</Text>
						<Button type="button" onClick={openFilter}>
							Choose Right Filter
						</Button>
					</Stack>
				) : (
					<Table
						bordered
						responsive
						hover
						headers={["", "Title", "Description"]}
						rows={items.map((tag) => {
							const { name, description, id } = tag
							return [
								<CheckboxInput
									id={name + id}
									checked={
										isSelectAll
											? !selectedItems.includes(id)
											: selectedItems.includes(id)
									}
									onChange={(e) => {
										const checked =
											(e.currentTarget.checked && isSelectAll) ||
											(!e.currentTarget.checked && !isSelectAll)

										if (checked) {
											setSelectedItems((ids) =>
												ids.filter((singleId) => singleId !== id)
											)
											decreaseIncrease(isSelectAll ? "increase" : "decrease")
										} else {
											setSelectedItems((ids) => [...ids, id])
											decreaseIncrease(!isSelectAll ? "increase" : "decrease")
										}
									}}
								/>,
								<Text>{name}</Text>,
								<Text>{description}</Text>,
							]
						})}
					/>
				)}

				{status.isLoading ? (
					<Inline justifyContent="center" alignItems="center">
						<Text>Loading more tags...</Text>
					</Inline>
				) : null}
			</Box>
		</Stack>
	)
}

export type TBatch = {
	id: string
	name: string
	totalJobs: number
	pendingJobs: number
	processedJobs: number
	progress: number
	failedJobs: number
	createdAt: string
	cancelledAt: string | null
	finishedAt: string | null
}

export function Batch({
	children,
	onFinish,
}: {
	children: (props: {
		init: (batch: TBatch) => void
		xhr: XHRInstance
		inProgress: boolean
	}) => React.ReactNode
	onFinish: () => void
}) {
	const xhr = useXHR()
	const [batch, setBatch] = useState<TBatch | null>(null)
	const { data: currentBatch } = useSWR(
		batch ? `/batch/${batch.id}` : null,
		() =>
			xhr
				.get<{ data: TBatch }>(`batches/${batch?.id}`)
				.then((resp) => resp.data.data)
				.then((batch) => {
					if (batch.finishedAt) {
						onFinish()
						setBatch(null)
					}

					return batch
				}),
		{
			refreshInterval: 1000,
		}
	)
	return (
		<>
			{children({
				xhr,
				init: (batch) => setBatch(batch),
				inProgress: Boolean(currentBatch && !currentBatch.finishedAt),
			})}
		</>
	)
}

export function SelectTags({ type, ...otherProps }: SelectTagsProps) {
	const xhr = useXHR()

	return (
		<AsyncSelect
			multiple
			creatable
			{...otherProps}
			fetch={(q) => TagsXHR(xhr).getTagsForSelect(type, q)}
		/>
	)
}
